import { useMemo, useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { useChartOfAccounts, useLedger } from "@/lib/finance/finance-hooks";
import { fmtFCFA } from "@/lib/format";
import { inputStyle } from "./_ui";

const CLASS_LABEL: Record<number, string> = {
	1: "Ressources durables",
	2: "Actifs immobilisés",
	3: "Stocks",
	4: "Tiers",
	5: "Trésorerie",
	6: "Charges",
	7: "Produits",
	8: "Autres",
};

/** FIN-06 — Plan de comptes SYSCOHADA + grand livre par compte. */
export function AdminChartPage() {
	const { data: accounts, isLoading } = useChartOfAccounts();
	const [q, setQ] = useState("");
	const [selected, setSelected] = useState<string | null>(null);
	const { data: ledger } = useLedger(selected ?? undefined);

	const filtered = useMemo(() => {
		const list = accounts ?? [];
		if (!q.trim()) return list;
		const s = q.toLowerCase();
		return list.filter(
			(a) => a.code.includes(s) || a.label.toLowerCase().includes(s),
		);
	}, [accounts, q]);

	return (
		<>
			<PageHead
				title="Plan de comptes SYSCOHADA"
				sub={`${accounts?.length ?? 0} comptes · révisé 2017 · comptes officiels verrouillés`}
			/>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: selected
						? "minmax(280px, 1fr) minmax(320px, 1.4fr)"
						: "1fr",
					gap: 16,
				}}
			>
				<Card>
					<CardHead
						title="Comptes"
						right={
							<input
								style={{ ...inputStyle, width: 180 }}
								placeholder="Filtrer…"
								value={q}
								onChange={(e) => setQ(e.target.value)}
							/>
						}
					/>
					<CardBody>
						<div
							className="table-wrap"
							style={{ margin: 0, maxHeight: 560, overflow: "auto" }}
						>
							<table className="table">
								<thead>
									<tr>
										<th>Code</th>
										<th>Libellé</th>
										<th>Classe</th>
										<th />
									</tr>
								</thead>
								<tbody>
									{isLoading && (
										<tr>
											<td
												colSpan={4}
												className="muted"
												style={{ textAlign: "center", padding: 24 }}
											>
												Chargement…
											</td>
										</tr>
									)}
									{filtered.map((a) => (
										<tr
											key={a.code}
											style={{
												cursor: "pointer",
												background:
													selected === a.code
														? "var(--a-hover, #f5f5f5)"
														: undefined,
											}}
											onClick={() => setSelected(a.code)}
										>
											<td className="mono">
												<strong>{a.code}</strong>
											</td>
											<td>{a.label}</td>
											<td>
												<Tag variant="neutral" noDot>
													{a.class} · {CLASS_LABEL[a.class]}
												</Tag>
											</td>
											<td>{a.ohadaLocked && <AdminIcon n="lock" s={12} />}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</CardBody>
				</Card>

				{selected && (
					<Card>
						<CardHead
							title={`Grand livre — ${selected}`}
							sub={ledger?.account?.label}
							right={
								<AdminButton
									variant="ghost"
									size="sm"
									onClick={() => setSelected(null)}
								>
									<AdminIcon n="close" s={13} />
								</AdminButton>
							}
						/>
						<CardBody>
							<div
								className="table-wrap"
								style={{ margin: 0, maxHeight: 560, overflow: "auto" }}
							>
								<table className="table">
									<thead>
										<tr>
											<th>Date</th>
											<th>Réf.</th>
											<th>Libellé</th>
											<th className="right">Débit</th>
											<th className="right">Crédit</th>
											<th className="right">Solde</th>
										</tr>
									</thead>
									<tbody>
										{(ledger?.rows ?? []).length === 0 && (
											<tr>
												<td
													colSpan={6}
													className="muted"
													style={{ textAlign: "center", padding: 24 }}
												>
													Aucun mouvement.
												</td>
											</tr>
										)}
										{(ledger?.rows ?? []).map((r, i) => (
											<tr key={i}>
												<td className="muted" style={{ fontSize: 12 }}>
													{r.postedAt}
												</td>
												<td className="mono" style={{ fontSize: 11 }}>
													{r.reference ?? "—"}
												</td>
												<td style={{ fontSize: 12 }}>
													{r.label}
													{r.letteringCode && (
														<Tag
															variant="success"
															noDot
															style={{ marginLeft: 4 }}
														>
															{r.letteringCode}
														</Tag>
													)}
												</td>
												<td className="right tn">
													{r.debit ? fmtFCFA(r.debit) : ""}
												</td>
												<td className="right tn">
													{r.credit ? fmtFCFA(r.credit) : ""}
												</td>
												<td className="right tn">
													<strong>{fmtFCFA(r.runningBalance)}</strong>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</CardBody>
					</Card>
				)}
			</div>
		</>
	);
}
